/**
 * PayslipModal — Modale d'édition de la fiche de paie mensuelle d'un employé.
 *
 * Fonctionnalités :
 *  - Chargement / calcul automatique via GET /salaries/{id}/payslip/{year}/{month}
 *  - Édition : jours d'absence, congés pris, primes, retenues manuelles, avance versée
 *  - Bons à crédit récupérés en LECTURE SEULE depuis /employee-orders (source de vérité)
 *  - Sauvegarde POST /salaries/{id}/payslip/{year}/{month}
 *  - Impression PDF (window.print sur une vue mise en page A4)
 */
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Save, Printer, Plus, Trash2, Loader2, ChevronLeft, ChevronRight, FileText, Lock, Unlock, AlertTriangle, Landmark, Bus, Link as LinkIcon, ShieldCheck } from "lucide-react";
import PayslipLinePresetPicker from "./PayslipLinePresetPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => (Math.round(v || 0)).toLocaleString('fr-FR');
const MONTHS = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];

export default function PayslipModal({ open, onClose, salaryId, initialYear, initialMonth, currentUser }) {
  const now = new Date();
  const [year, setYear] = useState(initialYear || now.getFullYear());
  const [month, setMonth] = useState(initialMonth || (now.getMonth() + 1));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);

  // Form fields (mirror of overrides)
  const [daysAbsent, setDaysAbsent] = useState(0);
  const [leaveTaken, setLeaveTaken] = useState(0);
  const [advance, setAdvance] = useState(0);
  const [bonuses, setBonuses] = useState([]);
  const [deductions, setDeductions] = useState([]);
  const [manualCredits, setManualCredits] = useState([]);
  const [availableSystemBons, setAvailableSystemBons] = useState([]);
  const [showBonPicker, setShowBonPicker] = useState(false);
  const [bonPickerSearch, setBonPickerSearch] = useState("");
  const [notes, setNotes] = useState("");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [transportOverride, setTransportOverride] = useState("");
  const pendingDatesRef = useRef(null);

  // Charge la config OHADA au premier ouvert
  useEffect(() => {
    if (!config) {
      axios.get(`${API}/salaries/config`).then(r => setConfig(r.data)).catch(() => {});
    }
  }, [config]);

  // Handlers : quand l'utilisateur choisit une date HORS du mois affiché,
  // on bascule automatiquement la fiche sur ce mois-là (auto-navigation)
  // en préservant les 2 dates saisies via le ref.
  const handlePeriodStartChange = (val) => {
    setPeriodStart(val);
    if (val) {
      const [y, m] = val.split("-").map((x) => parseInt(x, 10));
      if (y && m && (y !== year || m !== month)) {
        pendingDatesRef.current = { start: val, end: periodEnd };
        setYear(y); setMonth(m);
      }
    }
  };
  const handlePeriodEndChange = (val) => {
    setPeriodEnd(val);
    if (val && !periodStart) {
      const [y, m] = val.split("-").map((x) => parseInt(x, 10));
      if (y && m && (y !== year || m !== month)) {
        pendingDatesRef.current = { start: periodStart, end: val };
        setYear(y); setMonth(m);
      }
    }
  };

  const load = async () => {
    if (!salaryId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/salaries/${salaryId}/payslip/${year}/${month}`);
      const d = r.data;
      setData(d);
      setDaysAbsent(d.days_absent || 0);
      setLeaveTaken(d.leave_days_taken || 0);
      setAdvance(d.advance_paid || 0);
      setBonuses(d.bonuses || []);
      setDeductions(d.deductions || []);
      setManualCredits(d.manual_credits || []);
      setNotes(d.notes || "");
      setTransportOverride(""); // reset — la fiche recharge sa valeur d'origine (voir data.transport_allowance)
      // 🔄 (Feb 2026) Charge TOUS les bons authorisés/en attente (tous employés, tous mois)
      // → l'utilisateur cherche & sélectionne manuellement dans le picker
      try {
        const ordersRes = await axios.get(`${API}/employee-orders`, { params: { limit: 1000 } });
        const linkedIds = new Set((d.credit_orders || []).map(c => c.id));
        const alreadyManualLinked = new Set((d.manual_credits || []).filter(m => m.employee_order_id).map(m => m.employee_order_id));
        const available = (ordersRes.data?.orders || []).filter(o =>
          !linkedIds.has(o.id) &&
          !alreadyManualLinked.has(o.id) &&
          o.status !== 'rejected' &&
          o.status !== 'settled' &&
          o.status !== 'cancelled'
        );
        setAvailableSystemBons(available);
      } catch (e) {
        setAvailableSystemBons([]);
      }
      // Si l'utilisateur venait de saisir des dates hors mois (auto-nav),
      // on restaure ses valeurs — sinon on prend celles de la fiche.
      if (pendingDatesRef.current) {
        setPeriodStart(pendingDatesRef.current.start || "");
        setPeriodEnd(pendingDatesRef.current.end || "");
        pendingDatesRef.current = null;
      } else {
        setPeriodStart(d.period_start_date || "");
        setPeriodEnd(d.period_end_date || "");
      }
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); }, [open, salaryId, year, month]);

  // ── Calcul temps réel (aligné backend) ─────────────────────────────
  // Formule OHADA/Bénin :
  //   Brut = (dailyRate × jours) + primes + partie taxable transport
  //   CNSS employé (si soumis) = 3.6 % × min(brut, 500 000)
  //   Assiette ITS = Brut − CNSS
  //   ITS (si soumis) = barème progressif × (1 − abattements)
  //   Net = Brut + transport exonéré − CNSS − ITS − retenues − bons − avance
  const workDays = data?.work_days_per_month || 26;
  const dailyRate = data?.daily_rate || 0;
  const daysInMonth = new Date(year, month, 0).getDate();
  const monthStart = `${year}-${String(month).padStart(2, '0')}-01`;
  const monthEnd = `${year}-${String(month).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`;
  let daysInPeriod = workDays;
  let calendarDaysWorked = daysInMonth;
  if (periodStart || periodEnd) {
    const psStr = periodStart || monthStart;
    const peStr = periodEnd || monthEnd;
    const ps = new Date(psStr);
    const pe = new Date(peStr);
    const ms = new Date(monthStart);
    const me = new Date(monthEnd);
    const psC = ps < ms ? ms : ps;
    const peC = pe > me ? me : pe;
    if (!isNaN(psC) && !isNaN(peC) && peC >= psC) {
      calendarDaysWorked = Math.round((peC - psC) / 86400000) + 1;
      let working = 0;
      for (let i = 0; i < calendarDaysWorked; i++) {
        const d = new Date(psC.getTime() + i * 86400000);
        if (d.getDay() !== 0) working += 1;
      }
      daysInPeriod = working;
    } else {
      calendarDaysWorked = 0;
      daysInPeriod = 0;
    }
  }
  const daysPaid = Math.max(0, Math.round((daysInPeriod - daysAbsent) * 100) / 100);
  // Éviter la dérive d'arrondi : diviser directement le brut par les jours ouvrés
  const baseMonthly = data?.base_monthly || 0;
  const salaryBase = workDays > 0 ? Math.round(baseMonthly * daysPaid / workDays) : 0;
  const bonusTot = bonuses.reduce((s, b) => s + (parseFloat(b.amount) || 0), 0);
  const deductTot = deductions.reduce((s, d) => s + (parseFloat(d.amount) || 0), 0);
  const creditTot = data?.credit_orders_total || 0;
  const manualCredTot = manualCredits.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);

  // Transport (défaut employé, prorata si dates saisies, override possible sur la fiche)
  const baseTransport = transportOverride !== "" ? parseFloat(transportOverride) || 0 : (data?.transport_allowance || 0);
  // Note: le backend applique la proration; côté client on affiche tel quel car load() retourne déjà la valeur proratée
  const transportAmt = baseTransport;
  const transportCap = config?.transport_exemption_ceiling || 25000;
  const transportExempt = Math.min(transportAmt, transportCap);
  const transportTaxable = Math.max(0, transportAmt - transportExempt);

  const gross = Math.round(salaryBase + bonusTot + transportTaxable);
  const gainsTotal = Math.round(gross + transportExempt);

  // Cotisations OHADA (SI SOUMIS)
  const isSubjectCNSS = !!data?.subject_to_cnss;
  const isSubjectITS = !!data?.subject_to_its;
  const computeCNSS = (base) => {
    if (!isSubjectCNSS || base <= 0 || !config) return 0;
    return Math.round(Math.min(base, config.cnss_ceiling) * config.cnss_rate_employee);
  };
  const computeITS = (taxable) => {
    if (!isSubjectITS || taxable <= 0 || !config) return 0;
    let tax = 0, remaining = taxable, prev = 0;
    for (const b of config.its_brackets || []) {
      if (remaining <= 0) break;
      const ceiling = b.ceiling === null ? Infinity : b.ceiling;
      const band = Math.min(remaining, ceiling - prev);
      tax += band * b.rate;
      remaining -= band;
      prev = ceiling;
    }
    const idx = Math.min(Math.max(data?.employee?.children_count || 0, 0), 6);
    let reduction = (config.its_child_abatements || [0])[idx] || 0;
    const isMarried = String(data?.employee?.marital_status || "").toLowerCase().startsWith("mari");
    if (isMarried) reduction += config.its_married_abatement || 0;
    reduction = Math.min(reduction, config.its_abatement_cap || 0.35);
    return Math.round(Math.max(tax * (1 - reduction), 0));
  };
  const cnssEmp = computeCNSS(gross);
  const taxableNet = Math.max(0, gross - cnssEmp);
  const itsAmt = computeITS(taxableNet);

  const totalRetenues = cnssEmp + itsAmt + deductTot + creditTot + manualCredTot + (parseFloat(advance) || 0);
  const net = Math.round(gainsTotal - totalRetenues);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        days_absent: daysAbsent,
        leave_days_taken: leaveTaken,
        bonuses,
        deductions,
        advance_paid: parseFloat(advance) || 0,
        notes,
        period_start_date: periodStart || null,
        period_end_date: periodEnd || null,
        manual_credits: manualCredits,
      };
      if (transportOverride !== "") {
        payload.transport_allowance_override = parseFloat(transportOverride) || 0;
      }
      const r = await axios.post(`${API}/salaries/${salaryId}/payslip/${year}/${month}`, payload);
      setData(r.data);
      toast.success("Fiche de paie enregistrée");
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const promoteManualCredit = async (index) => {
    if (!window.confirm("Convertir ce bon manuel en bon officiel du système (Opérations → Bons à crédit) ?")) return;
    try {
      const r = await axios.post(`${API}/salaries/${salaryId}/payslip/${year}/${month}/promote-manual/${index}`);
      if (r.data?.payslip) {
        setData(r.data.payslip);
        setManualCredits(r.data.payslip.manual_credits || []);
      }
      toast.success("Bon promu en bon officiel du système");
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    }
  };

  const attachSystemBon = async (bonId) => {
    const bon = availableSystemBons.find(b => b.id === bonId);
    if (!bon) return;
    // Ajoute une entrée manual_credits liée à l'employee_order (label + amount + date + employee_order_id)
    setManualCredits([...manualCredits, {
      label: `[Bon système] ${(bon.items || []).map(i => i.name).join(', ') || 'Bon à crédit'}`,
      amount: bon.total || 0,
      date: (bon.created_at || "").slice(0, 10),
      employee_order_id: bon.id,
    }]);
    // Retire de la liste des disponibles
    setAvailableSystemBons(availableSystemBons.filter(b => b.id !== bonId));
    toast.success("Bon système rattaché — n'oubliez pas d'enregistrer");
  };

  const signBulletin = async () => {
    if (!data) return;
    // Sauver d'abord au cas où des changements non sauvés
    await save();
    if (!window.confirm("Verrouiller ce bulletin ? Une fois signé, toute modification sera détectée par la signature SHA-256.")) return;
    try {
      const r = await axios.post(`${API}/salaries/${salaryId}/payslip/${year}/${month}/sign`, {
        signed_by: currentUser?.username || currentUser?.name || "admin",
      });
      setData(r.data);
      toast.success("Bulletin verrouillé et signé");
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    }
  };

  const unsignBulletin = async () => {
    if (!data) return;
    if (!window.confirm("Retirer le verrouillage de ce bulletin ? Le hash de signature sera effacé.")) return;
    try {
      const r = await axios.post(`${API}/salaries/${salaryId}/payslip/${year}/${month}/unsign`);
      setData(r.data);
      toast.success("Verrouillage retiré");
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    }
  };

  const print = () => {
    if (!data) return;
    const win = window.open('', '_blank', 'width=800,height=1000');
    if (!win) {
      toast.error("Le navigateur a bloqué la fenêtre. Autorisez les pop-ups pour cette page.");
      return;
    }
    win.document.write(buildPrintHTML(data, {
      gross, daysPaid, bonusTot, deductTot, net, notes, advance,
      bonuses, deductions, leaveTaken, daysAbsent,
      periodStart, periodEnd, daysInPeriod, calendarDaysWorked, daysInMonth,
      // Nouveaux champs OHADA
      salaryBase, transportAmt, transportExempt, transportTaxable,
      cnssEmp, itsAmt, taxableNet, gainsTotal, totalRetenues,
      isSubjectCNSS, isSubjectITS,
    }));
    win.document.close();
    win.focus();
    // Attendre que le DOM + les images (logo, QR) soient chargés avant d'imprimer
    const doPrint = async () => {
      try {
        if (win.document.fonts?.ready) { try { await win.document.fonts.ready; } catch { /* noop */ } }
        const imgs = Array.from(win.document.images || []);
        await Promise.all(imgs.map((img) => img.complete ? Promise.resolve() : new Promise((res) => {
          img.onload = res; img.onerror = res;
          // safety timeout so a broken img doesn't block print
          setTimeout(res, 1500);
        })));
      } catch { /* noop */ }
      try { win.print(); } catch { /* popup closed by user */ }
    };
    // Petit délai pour que le navigateur applique le CSS après document.close()
    setTimeout(doPrint, 120);
  };

  const addBonusFromPreset = ({ label, amount }) => setBonuses((prev) => [...prev, { label, amount: amount || "" }]);
  const addDeductionFromPreset = ({ label, amount }) => setDeductions((prev) => [...prev, { label, amount: amount || "" }]);
  const updateLine = (list, setList, idx, field, val) => {
    const next = [...list];
    next[idx] = { ...next[idx], [field]: val };
    setList(next);
  };
  const removeLine = (list, setList, idx) => setList(list.filter((_, i) => i !== idx));

  const shiftMonth = (delta) => {
    let m = month + delta;
    let y = year;
    if (m < 1) { m = 12; y -= 1; } else if (m > 12) { m = 1; y += 1; }
    setMonth(m); setYear(y);
  };

  if (!open) return null;

  return (
    <div className="ps-root fixed inset-0 z-[9000] flex items-center justify-center p-2 sm:p-4" data-testid="payslip-modal">
      <style>{`
        .ps-root, .ps-root * { font-family: 'Space Grotesk', 'Outfit', system-ui, sans-serif; }
        .ps-lbl { font-size: 10px; font-weight: 700; letter-spacing: 0.13em; text-transform: uppercase; color: rgb(148, 163, 184); }
        .ps-inp {
          width: 100%; background: rgba(15, 23, 42, 0.8);
          border: 1px solid rgb(51, 65, 85);
          border-radius: 6px; padding: 6px 10px;
          color: white; font-size: 12.5px;
          font-family: 'JetBrains Mono', monospace;
        }
        .ps-inp:focus { outline: none; border-color: rgb(16, 185, 129); }
        .ps-row {
          display: flex; align-items: center; gap: 6px;
          padding: 6px 10px; border-radius: 8px;
          background: rgba(30, 41, 59, 0.5);
          border: 1px solid rgba(71, 85, 105, 0.3);
        }
        .ps-mono { font-family: 'JetBrains Mono', monospace; font-weight: 700; }
      `}</style>

      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[90vh] bg-slate-900 border border-slate-700 rounded-xl overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-800 bg-slate-900/70">
          <FileText className="w-5 h-5 text-emerald-400" strokeWidth={2.4} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-white font-black text-[15px] tracking-tight truncate">
                Fiche de paie {data?.employee?.name ? `— ${data.employee.name}` : ""}
              </p>
              {data?.signed && !data?.tampered && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-[9.5px] font-black uppercase tracking-wider" data-testid="payslip-signed-badge">
                  <Lock className="w-2.5 h-2.5" strokeWidth={3} /> Verrouillé
                </span>
              )}
              {data?.tampered && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-rose-500/15 border border-rose-500/40 text-rose-300 text-[9.5px] font-black uppercase tracking-wider" data-testid="payslip-tampered-badge">
                  <AlertTriangle className="w-2.5 h-2.5" strokeWidth={3} /> Altéré
                </span>
              )}
            </div>
            <p className="text-slate-500 text-[11px]">
              {MONTHS[month - 1]} {year} · {workDays}j ouvrés{(periodStart || periodEnd) ? ` · prorata ${daysInPeriod}j` : ""}
            </p>
          </div>
          <button onClick={() => shiftMonth(-1)} className="w-8 h-8 rounded-lg bg-slate-800 hover:bg-slate-700 flex items-center justify-center text-slate-300" title="Mois précédent" data-testid="payslip-prev-month">
            <ChevronLeft className="w-4 h-4" strokeWidth={2.4} />
          </button>
          <input
            type="month"
            value={`${year}-${String(month).padStart(2, '0')}`}
            onChange={(e) => {
              const v = e.target.value;
              if (!v) return;
              const [y, m] = v.split("-").map((x) => parseInt(x, 10));
              if (y && m) { setYear(y); setMonth(m); }
            }}
            className="h-8 px-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 text-[12px] font-semibold hover:bg-slate-700"
            title="Sélectionner le mois de paie"
            data-testid="payslip-month-picker"
          />
          <button onClick={() => shiftMonth(1)} className="w-8 h-8 rounded-lg bg-slate-800 hover:bg-slate-700 flex items-center justify-center text-slate-300" title="Mois suivant" data-testid="payslip-next-month">
            <ChevronRight className="w-4 h-4" strokeWidth={2.4} />
          </button>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-slate-800 hover:bg-slate-700 flex items-center justify-center text-slate-300" data-testid="payslip-close">
            <X className="w-4 h-4" strokeWidth={2.4} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement…
            </div>
          ) : !data ? (
            <p className="text-slate-500 text-center py-16">Aucune donnée.</p>
          ) : (
            <>
              {/* Alerte falsification si tampered */}
              {data?.tampered && (
                <div className="p-3 rounded-xl bg-rose-950/40 border border-rose-500/50 flex items-start gap-2" data-testid="payslip-tampered-warning">
                  <AlertTriangle className="w-4 h-4 text-rose-400 flex-shrink-0 mt-0.5" strokeWidth={2.4} />
                  <div className="text-[11.5px] text-rose-200 leading-snug">
                    <b>Signature invalide.</b> Le bulletin a été modifié après son verrouillage. La signature stockée ne correspond plus au contenu actuel. Contactez l&apos;administration.
                  </div>
                </div>
              )}

              {/* Bloc 0 : Période de travail effective (prorata mi-mois) */}
              <div className="p-3 rounded-xl bg-cyan-950/30 border border-cyan-500/30 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="ps-lbl text-cyan-300">Période de travail dans le mois</p>
                  {(periodStart || periodEnd) && (
                    <button
                      type="button"
                      onClick={() => { setPeriodStart(""); setPeriodEnd(""); }}
                      className="text-slate-500 hover:text-slate-300 text-[10.5px] underline"
                      data-testid="payslip-period-reset"
                    >
                      Mois complet
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="ps-lbl block mb-1">Date début</label>
                    <input
                      type="date"
                      value={periodStart}
                      onChange={(e) => handlePeriodStartChange(e.target.value)}
                      className="ps-inp"
                      data-testid="payslip-period-start"
                    />
                  </div>
                  <div>
                    <label className="ps-lbl block mb-1">Date fin</label>
                    <input
                      type="date"
                      value={periodEnd}
                      onChange={(e) => handlePeriodEndChange(e.target.value)}
                      className="ps-inp"
                      data-testid="payslip-period-end"
                    />
                  </div>
                </div>
                <p className="text-cyan-200/70 text-[10.5px] leading-snug">
                  {(periodStart || periodEnd)
                    ? `${calendarDaysWorked} jours de travail effectif × (${fmt(data?.base_monthly)} / ${workDays}) = ${fmt(dailyRate * calendarDaysWorked)} F`
                    : `Aucune date saisie = mois complet (${workDays} j).`}
                </p>
              </div>

              {/* Bloc 1 : Base & présence */}
              <div className="p-3 rounded-xl bg-slate-800/40 border border-slate-700/50 space-y-2.5">
                <p className="ps-lbl text-emerald-400">Présence &amp; base</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div>
                    <label className="ps-lbl block mb-1">Poste</label>
                    <p className="text-white text-[13px] font-semibold truncate">{data.employee?.role_title || "—"}</p>
                  </div>
                  <div>
                    <label className="ps-lbl block mb-1">Matricule</label>
                    <p className="text-white text-[13px] font-semibold truncate">{data.employee?.matricule || "—"}</p>
                  </div>
                  <div>
                    <label className="ps-lbl block mb-1">Salaire mensuel</label>
                    <p className="text-amber-300 ps-mono text-[13px]">{fmt(data.base_monthly)} F</p>
                  </div>
                  <div>
                    <label className="ps-lbl block mb-1">Taux journalier</label>
                    <p className="text-amber-300 ps-mono text-[13px]">{fmt(data.daily_rate)} F</p>
                  </div>
                  <div>
                    <label className="ps-lbl block mb-1">Jours absents</label>
                    <input type="number" min="0" max={workDays} value={daysAbsent} onChange={(e) => setDaysAbsent(parseInt(e.target.value) || 0)} className="ps-inp" data-testid="payslip-days-absent" />
                  </div>
                  <div>
                    <label className="ps-lbl block mb-1">Congés pris</label>
                    <input type="number" min="0" value={leaveTaken} onChange={(e) => setLeaveTaken(parseInt(e.target.value) || 0)} className="ps-inp" data-testid="payslip-leave-days" />
                  </div>
                  <div>
                    <label className="ps-lbl block mb-1">Jours de travail</label>
                    <p className="text-emerald-300 ps-mono text-[13px]" data-testid="payslip-days-paid">{daysPaid} / {(periodStart || periodEnd) ? daysInPeriod : workDays}</p>
                  </div>
                  <div>
                    <label className="ps-lbl block mb-1">Solde congés</label>
                    <p className="text-cyan-300 ps-mono text-[13px]">{data.leave_balance_days || 0} j</p>
                  </div>
                </div>
              </div>

              {/* Bloc 2 : Primes */}
              <div className="p-3 rounded-xl bg-slate-800/40 border border-slate-700/50 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="ps-lbl text-emerald-400">Primes &amp; bonus</p>
                  <PayslipLinePresetPicker kind="bonus" color="emerald" onAddLine={addBonusFromPreset} />
                </div>
                {bonuses.length === 0 ? (
                  <p className="text-slate-600 italic text-[11.5px] text-center py-2">Aucune prime</p>
                ) : bonuses.map((b, i) => (
                  <div key={i} className="ps-row !flex-col !items-stretch !gap-1.5" data-testid={`payslip-bonus-row-${i}`}>
                    <div className="flex items-center gap-1.5">
                      <span className="ps-lbl text-emerald-300 text-[9.5px] flex-shrink-0">Libellé</span>
                      <input
                        value={b.label || ""}
                        onChange={(e) => updateLine(bonuses, setBonuses, i, 'label', e.target.value)}
                        placeholder="Ex : PRIME EXCEPTIONNELLE"
                        className="ps-inp flex-1 !font-sans !text-white"
                        data-testid={`payslip-bonus-label-${i}`}
                      />
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="ps-lbl text-slate-500 text-[9.5px] flex-shrink-0">Montant</span>
                      <input
                        type="number"
                        value={b.amount}
                        onChange={(e) => updateLine(bonuses, setBonuses, i, 'amount', e.target.value)}
                        placeholder="0"
                        className="ps-inp flex-1 text-right"
                        data-testid={`payslip-bonus-amount-${i}`}
                      />
                      <span className="text-slate-400 text-[11px] font-bold">F</span>
                      <button
                        onClick={() => removeLine(bonuses, setBonuses, i)}
                        className="text-slate-500 hover:text-rose-400 p-1 flex-shrink-0"
                        title="Supprimer cette ligne"
                        data-testid={`payslip-bonus-remove-${i}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Bloc 2 bis : Prime de transport */}
              <div className="p-3 rounded-xl bg-sky-950/30 border border-sky-500/30 space-y-2" data-testid="payslip-transport-block">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <p className="ps-lbl text-sky-300 flex items-center gap-1.5"><Bus className="w-3 h-3" strokeWidth={2.6} /> Prime de transport</p>
                  <p className="text-slate-500 text-[10px]">Exonérée jusqu&apos;à {fmt(config?.transport_exemption_ceiling || 25000)} F</p>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="ps-lbl block mb-1">Montant</label>
                    <input
                      type="number"
                      value={transportOverride === "" ? (data?.transport_allowance ?? 0) : transportOverride}
                      onChange={(e) => setTransportOverride(e.target.value)}
                      disabled={data?.signed}
                      className="ps-inp"
                      data-testid="payslip-transport-input"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="ps-lbl block mb-1">Exonéré (ITS/CNSS)</label>
                    <p className="text-emerald-300 ps-mono text-[13px] pt-1.5">{fmt(transportExempt)} F</p>
                  </div>
                  <div>
                    <label className="ps-lbl block mb-1">Taxable (dans brut)</label>
                    <p className="text-amber-300 ps-mono text-[13px] pt-1.5">{fmt(transportTaxable)} F</p>
                  </div>
                </div>
              </div>

              {/* Bloc 3 : Retenues manuelles */}
              <div className="p-3 rounded-xl bg-slate-800/40 border border-slate-700/50 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="ps-lbl text-rose-400">Retenues manuelles</p>
                  <PayslipLinePresetPicker kind="deduction" color="rose" onAddLine={addDeductionFromPreset} />
                </div>
                {deductions.length === 0 ? (
                  <p className="text-slate-600 italic text-[11.5px] text-center py-2">Aucune retenue</p>
                ) : deductions.map((d, i) => (
                  <div key={i} className="ps-row !flex-col !items-stretch !gap-1.5" data-testid={`payslip-deduction-row-${i}`}>
                    <div className="flex items-center gap-1.5">
                      <span className="ps-lbl text-rose-300 text-[9.5px] flex-shrink-0">Libellé</span>
                      <input
                        value={d.label || ""}
                        onChange={(e) => updateLine(deductions, setDeductions, i, 'label', e.target.value)}
                        placeholder="Ex : ASSURANCE AGF"
                        className="ps-inp flex-1 !font-sans !text-white"
                        data-testid={`payslip-deduction-label-${i}`}
                      />
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="ps-lbl text-slate-500 text-[9.5px] flex-shrink-0">Montant</span>
                      <input
                        type="number"
                        value={d.amount}
                        onChange={(e) => updateLine(deductions, setDeductions, i, 'amount', e.target.value)}
                        placeholder="0"
                        className="ps-inp flex-1 text-right"
                        data-testid={`payslip-deduction-amount-${i}`}
                      />
                      <span className="text-slate-400 text-[11px] font-bold">F</span>
                      <button
                        onClick={() => removeLine(deductions, setDeductions, i)}
                        className="text-slate-500 hover:text-rose-400 p-1 flex-shrink-0"
                        title="Supprimer cette ligne"
                        data-testid={`payslip-deduction-remove-${i}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
                <div className="pt-1 flex items-center gap-2">
                  <label className="ps-lbl text-amber-400 flex-1">Avance déjà versée</label>
                  <input type="number" value={advance} onChange={(e) => setAdvance(e.target.value)} className="ps-inp w-32 text-right" data-testid="payslip-advance" />
                  <span className="text-amber-400 text-[11px] font-bold">F</span>
                </div>
              </div>

              {/* Bloc 3 bis : Cotisations OHADA (CNSS + ITS) — automatique si soumis */}
              <div className="p-3 rounded-xl bg-indigo-950/30 border border-indigo-500/30 space-y-2" data-testid="payslip-ohada-block">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <p className="ps-lbl text-indigo-300 flex items-center gap-1.5">
                    <Landmark className="w-3 h-3" strokeWidth={2.6} /> Cotisations OHADA / Bénin
                  </p>
                  <div className="flex items-center gap-1.5">
                    {isSubjectCNSS && (
                      <span className="px-1.5 py-0.5 rounded text-[9px] font-black uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">CNSS</span>
                    )}
                    {isSubjectITS && (
                      <span className="px-1.5 py-0.5 rounded text-[9px] font-black uppercase bg-amber-500/20 text-amber-300 border border-amber-500/30">ITS</span>
                    )}
                    {!isSubjectCNSS && !isSubjectITS && (
                      <span className="text-slate-500 text-[10px] italic">Employé non soumis</span>
                    )}
                  </div>
                </div>
                {(isSubjectCNSS || isSubjectITS) && (
                  <div className="space-y-1 text-[11.5px]">
                    {isSubjectCNSS && (
                      <div className="flex items-center justify-between">
                        <span className="text-slate-300">CNSS employé (3.6 % × brut plafonné {fmt(config?.cnss_ceiling || 500000)} F)</span>
                        <span className="text-rose-300 ps-mono" data-testid="payslip-cnss">− {fmt(cnssEmp)} F</span>
                      </div>
                    )}
                    {isSubjectITS && (
                      <>
                        <div className="flex items-center justify-between">
                          <span className="text-slate-500 text-[10.5px] italic">Assiette ITS (brut − CNSS)</span>
                          <span className="text-slate-400 ps-mono text-[10.5px]">{fmt(taxableNet)} F</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-slate-300">ITS (barème progressif Bénin)</span>
                          <span className="text-rose-300 ps-mono" data-testid="payslip-its">− {fmt(itsAmt)} F</span>
                        </div>
                      </>
                    )}
                    {isSubjectCNSS && (
                      <div className="flex items-center justify-between pt-1 mt-1 border-t border-indigo-500/20">
                        <span className="text-slate-500 text-[10.5px] italic">Charge patronale CNSS (info · non déduit)</span>
                        <span className="text-slate-500 ps-mono text-[10.5px]">{fmt(Math.round(Math.min(gross, config?.cnss_ceiling || 500000) * (config?.cnss_rate_employer || 0.154)))} F</span>
                      </div>
                    )}
                  </div>
                )}
                {!isSubjectCNSS && !isSubjectITS && (
                  <p className="text-slate-500 text-[11px] italic text-center py-1">
                    Activez « Soumis à la CNSS » ou « Soumis à l&apos;ITS » sur la fiche de l&apos;employé pour appliquer les cotisations.
                  </p>
                )}
              </div>

              {/* Bloc 4 : Bons à crédit — auto (employee-orders) + saisie manuelle */}
              <div className="p-3 rounded-xl bg-purple-950/30 border border-purple-500/30 space-y-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <p className="ps-lbl text-purple-300">Bons à crédit du mois</p>
                  <div className="flex items-center gap-2">
                    {/* 🆕 (Feb 2026) Bouton primaire "Lier un bon" — ouvre picker */}
                    <button
                      type="button"
                      onClick={() => setShowBonPicker(true)}
                      disabled={data?.signed}
                      className="px-2.5 py-1 rounded-md bg-purple-600 hover:bg-purple-500 text-white text-[11px] font-bold flex items-center gap-1 disabled:opacity-40"
                      data-testid="payslip-open-bon-picker"
                      title={`${availableSystemBons.length} bon(s) disponible(s) pour cet employé`}
                    >
                      <LinkIcon className="w-3 h-3" strokeWidth={2.6} />
                      Lier un bon {availableSystemBons.length > 0 && (
                        <span className="ml-0.5 px-1 rounded-full bg-white/25 text-white text-[9px] font-black">
                          {availableSystemBons.length}
                        </span>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setManualCredits([...manualCredits, { label: "", amount: 0, date: `${year}-${String(month).padStart(2,'0')}-${String(new Date().getDate()).padStart(2,'0')}` }])}
                      disabled={data?.signed}
                      className="text-purple-300 hover:text-purple-200 text-[11px] font-bold flex items-center gap-1 disabled:opacity-40"
                      data-testid="payslip-add-manual-credit"
                      title="Créer un bon manuel (non lié à l'existant)"
                    >
                      <Plus className="w-3 h-3" strokeWidth={2.6} />
                      Nouveau
                    </button>
                  </div>
                </div>

                {/* Bons AUTO (lecture seule depuis employee_orders) */}
                {data.credit_orders && data.credit_orders.length > 0 && (
                  <div className="space-y-1">
                    {data.credit_orders.map((c) => (
                      <div key={c.id} className="flex items-center gap-2 text-[11.5px]">
                        <span className="text-slate-500 font-mono">{c.date}</span>
                        <span className="text-slate-300 flex-1 truncate">{c.description}</span>
                        <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-purple-500/20 text-purple-300" title="Bon issu de Opérations → Bons à crédit">
                          auto
                        </span>
                        <span className="text-purple-300 ps-mono">{fmt(c.total)} F</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Bons MANUELS (saisie libre) */}
                {manualCredits.length > 0 && (
                  <div className="space-y-1 pt-1">
                    {manualCredits.map((c, i) => (
                      <div key={i} className="flex items-center gap-1.5">
                        <input
                          type="date"
                          value={c.date || ""}
                          onChange={(e) => { const arr = [...manualCredits]; arr[i] = { ...c, date: e.target.value }; setManualCredits(arr); }}
                          disabled={data?.signed || !!c.employee_order_id}
                          className="ps-inp !w-28 !text-[10.5px] !py-1 !px-1.5"
                        />
                        <input
                          value={c.label || ""}
                          onChange={(e) => { const arr = [...manualCredits]; arr[i] = { ...c, label: e.target.value }; setManualCredits(arr); }}
                          disabled={data?.signed || !!c.employee_order_id}
                          placeholder="Description du bon"
                          className="ps-inp !flex-1 !py-1"
                        />
                        <input
                          type="number"
                          value={c.amount || 0}
                          onChange={(e) => { const arr = [...manualCredits]; arr[i] = { ...c, amount: parseFloat(e.target.value) || 0 }; setManualCredits(arr); }}
                          disabled={data?.signed || !!c.employee_order_id}
                          className="ps-inp !w-24 !text-right !py-1"
                        />
                        {c.employee_order_id ? (
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-black uppercase bg-purple-500/25 text-purple-200 border border-purple-500/40" title="Rattaché à un bon officiel du système">
                            LIÉ
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => promoteManualCredit(i)}
                            disabled={data?.signed || !c.label || !c.amount}
                            className="text-emerald-300 hover:text-emerald-200 text-[10px] font-black disabled:opacity-40 px-1"
                            title="Convertir en bon officiel du système (Opérations)"
                          >
                            → SYS
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setManualCredits(manualCredits.filter((_, idx) => idx !== i))}
                          disabled={data?.signed}
                          className="w-6 h-6 rounded bg-rose-500/20 hover:bg-rose-500/40 text-rose-300 flex items-center justify-center disabled:opacity-40"
                        >
                          <Trash2 className="w-3 h-3" strokeWidth={2.4} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {(!data.credit_orders || data.credit_orders.length === 0) && manualCredits.length === 0 && (
                  <p className="text-slate-500 italic text-[11.5px] text-center py-2">Aucun bon à crédit ce mois — cliquez sur &quot;Nouveau bon&quot; ou rattachez un bon système existant.</p>
                )}

                {(creditTot > 0 || manualCredTot > 0) && (
                  <div className="flex items-center justify-between pt-1.5 mt-1.5 border-t border-purple-500/30">
                    <span className="text-purple-200 font-bold text-[12px]">Total bons à crédit</span>
                    <span className="text-purple-200 ps-mono text-[13px]">{fmt(creditTot + manualCredTot)} F</span>
                  </div>
                )}
              </div>

              {/* Notes */}
              <div className="p-3 rounded-xl bg-slate-800/40 border border-slate-700/50">
                <label className="ps-lbl block mb-1">Notes</label>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="ps-inp !font-sans" placeholder="Commentaire libre pour la fiche" />
              </div>

              {/* Récap total OHADA — Structure : Gains → Retenues → Net */}
              <div className="p-4 rounded-xl bg-gradient-to-br from-emerald-950/70 to-teal-950/50 border-2 border-emerald-500/40 space-y-1">
                <p className="ps-lbl text-emerald-300/80 mb-1">Récapitulatif</p>
                <div className="flex justify-between text-slate-300 text-[12px]"><span>Salaire base ({daysPaid} j × {fmt(dailyRate)})</span><span className="ps-mono">{fmt(salaryBase)} F</span></div>
                {bonusTot > 0 && <div className="flex justify-between text-emerald-300 text-[12px]"><span>+ Primes</span><span className="ps-mono">{fmt(bonusTot)} F</span></div>}
                {transportTaxable > 0 && <div className="flex justify-between text-sky-300 text-[12px]"><span>+ Transport (taxable)</span><span className="ps-mono">{fmt(transportTaxable)} F</span></div>}
                <div className="flex justify-between text-white font-bold text-[12.5px] pt-1 mt-1 border-t border-emerald-500/20"><span>= Salaire brut imposable</span><span className="ps-mono">{fmt(gross)} F</span></div>
                {transportExempt > 0 && <div className="flex justify-between text-sky-300 text-[12px]"><span>+ Transport (exonéré)</span><span className="ps-mono">{fmt(transportExempt)} F</span></div>}
                <div className="flex justify-between text-white font-bold text-[13px]"><span>= Gains totaux</span><span className="ps-mono">{fmt(gainsTotal)} F</span></div>
                {isSubjectCNSS && <div className="flex justify-between text-rose-300 text-[12px] pt-1"><span>− CNSS employé (3.6%)</span><span className="ps-mono">{fmt(cnssEmp)} F</span></div>}
                {isSubjectITS && <div className="flex justify-between text-rose-300 text-[12px]"><span>− ITS (barème progressif)</span><span className="ps-mono">{fmt(itsAmt)} F</span></div>}
                {deductTot > 0 && <div className="flex justify-between text-rose-300 text-[12px]"><span>− Retenues manuelles</span><span className="ps-mono">{fmt(deductTot)} F</span></div>}
                {(creditTot + manualCredTot) > 0 && <div className="flex justify-between text-purple-300 text-[12px]"><span>− Bons crédit</span><span className="ps-mono">{fmt(creditTot + manualCredTot)} F</span></div>}
                {(parseFloat(advance) || 0) > 0 && <div className="flex justify-between text-amber-300 text-[12px]"><span>− Avance</span><span className="ps-mono">{fmt(parseFloat(advance) || 0)} F</span></div>}
                <div className="flex justify-between text-white font-black text-[16px] pt-2 mt-1 border-t border-emerald-500/40">
                  <span>NET À PAYER</span>
                  <span className="ps-mono text-emerald-300" data-testid="payslip-net">{fmt(net)} F</span>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer actions */}
        <div className="flex items-center gap-2 px-4 py-3 border-t border-slate-800 bg-slate-900/70 flex-wrap">
          <button onClick={print} disabled={!data} className="flex items-center gap-1.5 h-9 px-3 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold text-[12.5px] disabled:opacity-50" data-testid="payslip-print">
            <Printer className="w-3.5 h-3.5" strokeWidth={2.4} />
            Imprimer PDF
          </button>
          {data?.signed ? (
            <button onClick={unsignBulletin} disabled={!data} className="flex items-center gap-1.5 h-9 px-3 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold text-[12.5px] disabled:opacity-50" data-testid="payslip-unsign" title="Retirer le verrouillage">
              <Unlock className="w-3.5 h-3.5" strokeWidth={2.4} />
              Déverrouiller
            </button>
          ) : (
            <button onClick={signBulletin} disabled={!data} className="flex items-center gap-1.5 h-9 px-3 rounded-lg text-slate-900 font-bold text-[12.5px] disabled:opacity-50" style={{ background: '#a92e2e', color: 'white' }} data-testid="payslip-sign" title="Verrouiller et signer avec SHA-256">
              <Lock className="w-3.5 h-3.5" strokeWidth={2.6} />
              Verrouiller & signer
            </button>
          )}
          <div className="flex-1" />
          <button onClick={save} disabled={saving || data?.signed} className="flex items-center gap-1.5 h-9 px-4 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-bold text-[12.5px] disabled:opacity-50" data-testid="payslip-save" title={data?.signed ? "Bulletin verrouillé — déverrouillez pour modifier" : ""}>
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" strokeWidth={2.6} />}
            Enregistrer
          </button>
        </div>
      </div>

      {/* 🆕 (Feb 2026) Modale picker : Lier un bon à crédit existant */}
      {showBonPicker && (() => {
        const empName = (data?.employee?.name || "").toLowerCase();
        const q = bonPickerSearch.trim().toLowerCase();
        // Normalisation pour matching approximatif du nom employé
        const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9\s]/g, " ").trim();
        const empNorm = norm(empName);
        // Tri : bons de l'employé courant d'abord, puis les autres
        const sortedBons = [...availableSystemBons].sort((a, b) => {
          const aMatches = empNorm && norm(a.employee_name || "").includes(empNorm.split(" ")[0]);
          const bMatches = empNorm && norm(b.employee_name || "").includes(empNorm.split(" ")[0]);
          if (aMatches !== bMatches) return aMatches ? -1 : 1;
          return (b.created_at || "").localeCompare(a.created_at || "");
        });
        const filtered = sortedBons.filter(b => {
          if (!q) return true;
          const hay = [
            b.employee_name, b.notes, b.month_period, b.status,
            ...(b.items || []).map(i => i.name),
          ].join(" ").toLowerCase();
          return hay.includes(q);
        });
        return (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70"
          onClick={() => setShowBonPicker(false)}
        >
          <div
            className="w-full max-w-3xl max-h-[85vh] bg-slate-900 rounded-xl border border-purple-500/40 shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-purple-500/20">
              <div className="min-w-0 flex-1">
                <p className="text-purple-300 text-[15px] font-black flex items-center gap-2">
                  <LinkIcon className="w-4 h-4" strokeWidth={2.6} />
                  Lier un bon à crédit
                </p>
                <p className="text-slate-500 text-[11.5px] mt-0.5">
                  Tous les bons du système — les bons pour <b className="text-slate-300">{data?.employee?.name || "cet employé"}</b> apparaissent en premier
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowBonPicker(false)}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-800 flex-shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {/* Recherche */}
            <div className="px-4 pt-3">
              <div className="relative">
                <Landmark className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  autoFocus
                  value={bonPickerSearch}
                  onChange={(e) => setBonPickerSearch(e.target.value)}
                  placeholder="Rechercher par nom d'employé, article, mois…"
                  className="w-full h-9 pl-9 pr-3 rounded-lg bg-slate-800/70 border border-slate-700 text-slate-200 text-[13px] placeholder:text-slate-500 focus:outline-none focus:border-purple-500/60"
                  data-testid="payslip-bon-picker-search"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
              {filtered.length === 0 ? (
                <div className="p-8 text-center text-slate-500 text-[12.5px]">
                  <ShieldCheck className="w-8 h-8 mx-auto mb-2 text-slate-700" />
                  {q ? `Aucun bon trouvé pour « ${bonPickerSearch} »` : "Aucun bon à crédit disponible dans le système."}
                  <p className="text-slate-600 text-[11px] mt-1">
                    Les bons doivent être « autorisés » ou « en attente » et non déjà réglés.
                  </p>
                </div>
              ) : (
                filtered.map(b => {
                  const items = (b.items || []).map(i => i.name).join(', ');
                  const period = b.month_period || (b.created_at || '').slice(0, 7);
                  const badgeColor = b.status === 'authorized'
                    ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
                    : 'bg-amber-500/20 text-amber-300 border-amber-500/30';
                  const matchesEmp = empNorm && norm(b.employee_name || "").includes(empNorm.split(" ")[0]);
                  return (
                    <button
                      key={b.id}
                      type="button"
                      onClick={() => {
                        attachSystemBon(b.id);
                        setShowBonPicker(false);
                        setBonPickerSearch("");
                      }}
                      className={`w-full text-left p-2.5 rounded-lg border transition-colors ${
                        matchesEmp
                          ? 'bg-purple-500/10 border-purple-500/40 hover:bg-purple-500/20'
                          : 'bg-slate-800/50 border-slate-700 hover:bg-purple-500/15 hover:border-purple-500/50'
                      }`}
                      data-testid={`payslip-pick-bon-${b.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className={`font-black text-[12.5px] truncate ${matchesEmp ? 'text-purple-200' : 'text-slate-200'}`}>
                              {b.employee_name || "(sans nom)"}
                              {b.employee_position && <span className="text-slate-500 font-normal text-[10.5px] ml-1">· {b.employee_position}</span>}
                            </span>
                            {matchesEmp && (
                              <span className="px-1.5 py-0.5 rounded text-[9px] font-black uppercase bg-purple-500/30 text-purple-200 border border-purple-500/40">
                                Cet employé
                              </span>
                            )}
                          </div>
                          <p className="text-slate-300 text-[12px] mt-0.5 truncate">
                            {items || "Bon à crédit"}
                          </p>
                          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                            <span className={`px-1.5 py-0.5 rounded text-[9px] font-black uppercase border ${badgeColor}`}>
                              {b.status === 'authorized' ? 'Autorisé' : b.status}
                            </span>
                            <span className="text-slate-500 text-[10.5px]">
                              {period} · {(b.created_at || '').slice(0, 10)}
                            </span>
                            {b.notes && <span className="text-slate-500 text-[10.5px] italic">· {b.notes.slice(0, 40)}</span>}
                          </div>
                        </div>
                        <span className="text-purple-300 font-mono font-black text-[14px] whitespace-nowrap">
                          {fmt(b.total)} F
                        </span>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
            <div className="p-3 border-t border-purple-500/20 flex items-center justify-between text-[11px] text-slate-500">
              <span>{filtered.length} / {availableSystemBons.length} bon(s) affiché(s)</span>
              <button
                type="button"
                onClick={() => setShowBonPicker(false)}
                className="px-3 h-8 rounded-md bg-slate-700 hover:bg-slate-600 text-slate-200 text-[12px] font-semibold"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
}

function buildPrintHTML(d, extra) {
  const emp = d.employee || {};
  const bulletin_no = d.bulletin_number || `MAXO-${d.year}-${String(d.month).padStart(2,'0')}`;
  return `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Bulletin ${bulletin_no} — ${emp.name || ''}</title>
<style>${PAYSLIP_PRINT_CSS}</style></head><body>
${buildPayslipBody(d, extra)}
</body></html>`;
}

export function buildPayslipBody(d, extra) {
  const emp = d.employee || {};
  const co = d.company || {};
  const bulletin_no = d.bulletin_number || `MAXO-${d.year}-${String(d.month).padStart(2,'0')}`;
  const printed = new Date().toLocaleDateString('fr-FR');
  const logoUrl = `${window.location.origin}/logo_espace_maxo.png`;
  const verifyUrl = `${window.location.origin}/verify-bulletin/${bulletin_no}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=110x110&margin=2&data=${encodeURIComponent(verifyUrl)}`;
  // Formattage FR : espaces insécables comme séparateurs de milliers
  const fmtMoney = (v) => {
    const n = Math.round(Number(v) || 0);
    return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0');
  };
  const fmtDate = (iso) => {
    if (!iso) return "—";
    const s = String(iso).slice(0, 10);
    const [y, m, day] = s.split("-");
    return y && m && day ? `${day}/${m}/${y}` : iso;
  };
  // Montant en lettres (français) — pour signature légale
  const numberToWordsFR = (num) => {
    if (num === 0) return "zéro";
    const units = ["", "un", "deux", "trois", "quatre", "cinq", "six", "sept", "huit", "neuf", "dix", "onze", "douze", "treize", "quatorze", "quinze", "seize", "dix-sept", "dix-huit", "dix-neuf"];
    const tens = ["", "", "vingt", "trente", "quarante", "cinquante", "soixante", "soixante", "quatre-vingt", "quatre-vingt"];
    const under100 = (n) => {
      if (n < 20) return units[n];
      const t = Math.floor(n / 10), u = n % 10;
      if (t === 7 || t === 9) {
        const base = tens[t];
        const extra = 10 + u;
        return (t === 7 && u === 1) ? "soixante-et-onze" : `${base}-${units[extra]}`;
      }
      if (t === 8 && u === 0) return "quatre-vingts";
      if (u === 0) return tens[t];
      if (u === 1 && t !== 8) return `${tens[t]}-et-un`;
      return `${tens[t]}-${units[u]}`;
    };
    const under1000 = (n) => {
      if (n < 100) return under100(n);
      const c = Math.floor(n / 100), r = n % 100;
      let cent = c === 1 ? "cent" : `${units[c]} cent`;
      if (c > 1 && r === 0) cent += "s";
      return r === 0 ? cent : `${cent} ${under100(r)}`;
    };
    const parts = [];
    const millions = Math.floor(num / 1000000);
    const thousands = Math.floor((num % 1000000) / 1000);
    const rest = num % 1000;
    if (millions > 0) parts.push(millions === 1 ? "un million" : `${under1000(millions)} millions`);
    if (thousands > 0) parts.push(thousands === 1 ? "mille" : `${under1000(thousands)} mille`);
    if (rest > 0) parts.push(under1000(rest));
    return parts.join(" ");
  };
  const netInWords = (() => {
    const w = numberToWordsFR(Math.round(extra.net || 0));
    return w.charAt(0).toUpperCase() + w.slice(1) + " francs CFA";
  })();

  const kv = (label, value) => `<div style="display:flex;justify-content:space-between;padding:3px 0;font-size:10.5px;border-bottom:1px dotted #94a3b8"><span style="color:#334155;font-weight:600">${label}</span><span style="font-weight:800;color:#0f172a">${value || '—'}</span></div>`;
  const creditRows = (d.credit_orders || []).map(c => `<tr><td style="padding:4px 8px;font-size:10.5px;color:#1e293b;font-weight:500">${fmtDate(c.date)}</td><td style="padding:4px 8px;font-size:10.5px;color:#0f172a;font-weight:500">${c.description}</td><td style="padding:4px 8px;text-align:right;font-family:monospace;font-size:10.5px;color:#0f172a;font-weight:700">${fmtMoney(c.total)} F</td></tr>`).join('');
  const manualCredRows = (d.manual_credits || []).map(c => `<tr><td style="padding:4px 8px;font-size:10.5px;color:#1e293b;font-weight:500">${fmtDate(c.date)}</td><td style="padding:4px 8px;font-size:10.5px;color:#0f172a;font-weight:500">${c.label || '(bon manuel)'}</td><td style="padding:4px 8px;text-align:right;font-family:monospace;font-size:10.5px;color:#0f172a;font-weight:700">${fmtMoney(c.amount)} F</td></tr>`).join('');
  const allCredRows = creditRows + manualCredRows || '<tr><td colspan="3" style="padding:5px 8px;font-style:italic;color:#475569;text-align:center;font-size:10.5px;font-weight:500">Aucun bon à crédit ce mois</td></tr>';
  const totalCreditsAll = (d.credit_orders_total || 0) + (d.manual_credits_total || 0);

  return `
  <!-- ENTÊTE -->
  <div class="brand">
    <div class="brand-l">
      <img src="${logoUrl}" alt="Espace Maxo" />
      <div>
        <p class="co-name">${co.name || 'ESPACE MAXO SARL'}</p>
        <div class="co-meta">
          ${co.tagline || 'Restaurant & Jeux VR'}<br/>
          ${co.address || ''}<br/>
          IFU : ${co.ifu || '—'} · RCCM : ${co.rccm || '—'}${co.phone ? '<br/>Tél : ' + co.phone : ''}
        </div>
      </div>
    </div>
    <div class="bulletin-box">
      <div class="lbl">Bulletin de paie</div>
      <div class="num">${bulletin_no}</div>
      <div class="period">${MONTHS[d.month - 1]} ${d.year}</div>
    </div>
  </div>

  <!-- IDENTITÉ EMPLOYÉ -->
  <div class="grid2">
    <div class="card">
      <div class="card-title">Employé</div>
      ${kv('Nom & Prénom', `<b>${emp.name || '—'}</b>`)}
      ${kv('Matricule', emp.matricule)}
      ${kv('Poste', emp.role_title)}
      ${emp.contract_type ? kv('Type de contrat', emp.contract_type) : ''}
      ${kv("Date d'embauche", fmtDate(emp.start_date))}
      ${emp.marital_status ? kv('Situation familiale', `${emp.marital_status}${emp.children_count ? ' · ' + emp.children_count + ' enfant(s)' : ''}`) : ''}
      ${emp.phone ? kv('Téléphone', emp.phone) : ''}
      ${emp.cnss_number ? kv('N° CNSS', emp.cnss_number) : ''}
      ${emp.mobile_money ? kv('Mobile Money', emp.mobile_money) : ''}
    </div>
    <div class="card">
      <div class="card-title">Période & base</div>
      ${kv('Période travaillée', (extra.periodStart || extra.periodEnd) ? `du ${fmtDate(extra.periodStart)} au ${fmtDate(extra.periodEnd)}` : 'Mois complet')}
      ${kv('Jours de travail effectifs', `<b>${extra.daysPaid} jour(s)</b>`)}
      ${kv('Taux journalier', `${fmtMoney(d.daily_rate)} F/j (÷ ${d.work_days_per_month})`)}
      ${kv('Salaire mensuel de base', `${fmtMoney(d.base_monthly)} F`)}
      ${extra.daysAbsent > 0 ? kv("Jours d'absence", `${extra.daysAbsent} j`) : ''}
      ${extra.leaveTaken > 0 ? kv('Congés pris (payés)', `${extra.leaveTaken} j`) : ''}
    </div>
  </div>

  <!-- BULLETIN OHADA — Tableau 4 colonnes GAINS / RETENUES -->
  <h2>Bulletin de paie détaillé (OHADA)</h2>
  <table class="no-break" style="border:1.5px solid #64748b;border-radius:5px;overflow:hidden">
    <thead>
      <tr style="background:#0F1F3D;color:white;font-size:10px;text-transform:uppercase;letter-spacing:0.1em;font-weight:800">
        <td style="padding:5px 8px;width:44%">Rubrique</td>
        <td style="padding:5px 8px;text-align:right;width:20%">Base</td>
        <td style="padding:5px 8px;text-align:right;width:12%">Taux</td>
        <td style="padding:5px 8px;text-align:right;width:24%">Montant</td>
      </tr>
    </thead>
    <tbody>
      <tr style="background:#d1fae5"><td colspan="4" style="padding:4px 8px;font-weight:900;color:#064e3b;font-size:10px;text-transform:uppercase;letter-spacing:0.08em">Gains</td></tr>
      <tr><td style="padding:4px 8px;font-size:11px;color:#0f172a;font-weight:500">Salaire de base</td><td style="padding:4px 8px;text-align:right;font-family:monospace;font-size:10.5px;color:#1e293b;font-weight:600">${extra.daysPaid} j × ${fmtMoney(d.daily_rate)}</td><td style="padding:4px 8px;text-align:right;font-size:10px;color:#334155">—</td><td style="padding:4px 8px;text-align:right;font-family:monospace;font-weight:800;color:#064e3b;font-size:11px">${fmtMoney(extra.salaryBase || extra.gross)} F</td></tr>
      ${(extra.bonuses || []).map(b => `<tr><td style="padding:4px 8px;font-size:11px;color:#0f172a;font-weight:500">+ ${b.label || '(prime)'}</td><td colspan="2" style="padding:4px 8px;text-align:right;color:#334155;font-size:10px;font-weight:600">Prime</td><td style="padding:4px 8px;text-align:right;font-family:monospace;font-weight:800;color:#064e3b;font-size:11px">${fmtMoney(b.amount)} F</td></tr>`).join('')}
      ${(extra.transportAmt || 0) > 0 ? `
        ${(extra.transportTaxable || 0) > 0 ? `<tr><td style="padding:4px 8px;font-size:11px;color:#0f172a;font-weight:500">Prime de transport (taxable)</td><td colspan="2" style="padding:4px 8px;text-align:right;color:#334155;font-size:10px;font-weight:600">Excédent > ${fmtMoney(25000)} F</td><td style="padding:4px 8px;text-align:right;font-family:monospace;font-weight:800;color:#064e3b;font-size:11px">${fmtMoney(extra.transportTaxable)} F</td></tr>` : ''}
        ${(extra.transportExempt || 0) > 0 ? `<tr style="background:#dbeafe"><td style="padding:4px 8px;font-size:11px;color:#0f172a;font-weight:500">Prime de transport (exonérée)</td><td colspan="2" style="padding:4px 8px;text-align:right;color:#0c4a6e;font-size:10px;font-weight:700">Exonérée CNSS/ITS</td><td style="padding:4px 8px;text-align:right;font-family:monospace;font-weight:800;color:#0c4a6e;font-size:11px">${fmtMoney(extra.transportExempt)} F</td></tr>` : ''}
      ` : ''}
      <tr style="background:#e2e8f0;border-top:2px solid #64748b"><td style="padding:5px 8px;font-weight:900;font-size:11px;color:#0f172a">= Salaire brut imposable</td><td colspan="2"></td><td style="padding:5px 8px;text-align:right;font-family:monospace;font-weight:900;font-size:12px;color:#0F1F3D">${fmtMoney(extra.gross)} F</td></tr>
      <tr style="background:#dbeafe"><td style="padding:5px 8px;font-weight:900;font-size:11px;color:#0f172a">= GAINS TOTAUX</td><td colspan="2"></td><td style="padding:5px 8px;text-align:right;font-family:monospace;font-weight:900;font-size:12px;color:#0F1F3D">${fmtMoney(extra.gainsTotal || extra.gross)} F</td></tr>

      <tr style="background:#fee2e2"><td colspan="4" style="padding:4px 8px;font-weight:900;color:#7f1d1d;font-size:10px;text-transform:uppercase;letter-spacing:0.08em">Retenues</td></tr>
      ${extra.isSubjectCNSS ? `<tr><td style="padding:4px 8px;font-size:11px;color:#0f172a;font-weight:500">− CNSS employé</td><td style="padding:4px 8px;text-align:right;font-family:monospace;font-size:10.5px;color:#1e293b;font-weight:600">${fmtMoney(Math.min(extra.gross, 500000))} F</td><td style="padding:4px 8px;text-align:right;font-family:monospace;font-size:10.5px;color:#1e293b;font-weight:600">3.60 %</td><td style="padding:4px 8px;text-align:right;font-family:monospace;font-weight:800;color:#7f1d1d;font-size:11px">${fmtMoney(extra.cnssEmp)} F</td></tr>` : ''}
      ${extra.isSubjectITS ? `<tr><td style="padding:4px 8px;font-size:11px;color:#0f172a;font-weight:500">− ITS (impôt sur salaires)</td><td style="padding:4px 8px;text-align:right;font-family:monospace;font-size:10.5px;color:#1e293b;font-weight:600">${fmtMoney(extra.taxableNet)} F</td><td style="padding:4px 8px;text-align:right;font-family:monospace;font-size:10px;color:#334155;font-weight:600">Progressif</td><td style="padding:4px 8px;text-align:right;font-family:monospace;font-weight:800;color:#7f1d1d;font-size:11px">${fmtMoney(extra.itsAmt)} F</td></tr>` : ''}
      ${(extra.deductions || []).map(x => `<tr><td style="padding:4px 8px;font-size:11px;color:#0f172a;font-weight:500">− ${x.label || '(retenue)'}</td><td colspan="2" style="padding:4px 8px;text-align:right;color:#334155;font-size:10px;font-weight:600">Manuelle</td><td style="padding:4px 8px;text-align:right;font-family:monospace;font-weight:800;color:#7f1d1d;font-size:11px">${fmtMoney(x.amount)} F</td></tr>`).join('')}
      ${(parseFloat(extra.advance) || 0) > 0 ? `<tr><td style="padding:4px 8px;font-size:11px;color:#0f172a;font-weight:500">− Avance déjà versée</td><td colspan="2" style="padding:4px 8px;text-align:right;color:#334155;font-size:10px;font-weight:600">—</td><td style="padding:4px 8px;text-align:right;font-family:monospace;font-weight:800;color:#7f1d1d;font-size:11px">${fmtMoney(extra.advance)} F</td></tr>` : ''}
      ${(d.credit_orders_total || 0) + (d.manual_credits_total || 0) > 0 ? `<tr><td style="padding:4px 8px;font-size:11px;color:#0f172a;font-weight:500">− Bons à crédit du mois</td><td colspan="2" style="padding:4px 8px;text-align:right;color:#334155;font-size:10px;font-weight:600">Voir détail ci-dessous</td><td style="padding:4px 8px;text-align:right;font-family:monospace;font-weight:800;color:#7f1d1d;font-size:11px">${fmtMoney((d.credit_orders_total || 0) + (d.manual_credits_total || 0))} F</td></tr>` : ''}
      <tr style="background:#fecaca;border-top:2px solid #dc2626"><td style="padding:5px 8px;font-weight:900;font-size:11px;color:#0f172a">= Total retenues</td><td colspan="2"></td><td style="padding:5px 8px;text-align:right;font-family:monospace;font-weight:900;font-size:12px;color:#7f1d1d">${fmtMoney(extra.totalRetenues)} F</td></tr>
    </tbody>
  </table>

  ${extra.isSubjectCNSS ? `<div style="margin-top:4px;padding:5px 10px;background:#e2e8f0;border-left:3px solid #64748b;border-radius:3px;font-size:10px;color:#0f172a;font-style:italic;font-weight:500">Info : charge patronale CNSS (15.4 % × ${fmtMoney(Math.min(extra.gross, 500000))} F = ${fmtMoney(Math.round(Math.min(extra.gross, 500000) * 0.154))} F) — non déduite du net.</div>` : ''}

  ${((d.credit_orders && d.credit_orders.length > 0) || (d.manual_credits && d.manual_credits.length > 0)) ? `
  <h2>Bons à crédit du mois</h2>
  <table style="border:1.5px solid #94a3b8;border-radius:5px;overflow:hidden">
    <thead><tr style="background:#e9d5ff;color:#4c1d95;font-weight:800;font-size:10px;text-transform:uppercase;letter-spacing:0.08em"><td style="padding:4px 8px">Date</td><td style="padding:4px 8px">Description</td><td style="padding:4px 8px;text-align:right">Montant</td></tr></thead>
    <tbody>${allCredRows}</tbody>
    <tfoot><tr style="background:#ddd6fe"><td colspan="2" style="padding:5px 8px;font-weight:800;color:#4c1d95;font-size:10.5px">Total bons à crédit</td><td style="padding:5px 8px;text-align:right;font-family:monospace;font-weight:900;color:#4c1d95;font-size:11.5px">− ${fmtMoney(totalCreditsAll)} F</td></tr></tfoot>
  </table>
  ` : ''}

  <!-- NET À PAYER -->
  <div class="net-box no-break">
    <div style="flex:1">
      <div class="lbl">Net à payer — ${MONTHS[d.month - 1]} ${d.year}</div>
      <div style="font-size:10.5px;font-style:italic;opacity:1;margin-top:3px;line-height:1.4;font-weight:600">
        Arrêté à la somme de <b>${netInWords}</b>
      </div>
      ${extra.notes ? `<div style="font-size:10px;opacity:0.95;margin-top:3px;font-weight:500">${extra.notes}</div>` : ''}
    </div>
    <div class="val">${fmtMoney(extra.net)} F CFA</div>
  </div>

  <!-- SIGNATURES -->
  <div class="sig-block no-break">
    <div class="sig">
      <div style="font-weight:800;color:#7f1d1d;font-size:10.5px;letter-spacing:0.1em;text-transform:uppercase">Employeur</div>
      <div class="line">${co.name || 'ESPACE MAXO SARL'}</div>
    </div>
    <div class="sig">
      <div style="font-weight:800;color:#7f1d1d;font-size:10.5px;letter-spacing:0.1em;text-transform:uppercase;text-align:right">Employé — bon pour accord</div>
      <div class="line">${emp.name || ''}</div>
    </div>
  </div>

  <!-- FOOTER LÉGAL + QR code de vérification -->
  <div class="no-break" style="margin-top:8px;display:flex;align-items:center;gap:10px;padding:7px 12px;border:1.5px solid #94a3b8;border-radius:5px;background:#f1f5f9">
    <img src="${qrUrl}" alt="QR vérification" style="width:52px;height:52px;flex-shrink:0" />
    <div style="flex:1;font-size:10px;color:#0f172a;line-height:1.5;font-weight:500">
      <div style="font-weight:900;color:#0F1F3D;font-size:10.5px;letter-spacing:0.1em;text-transform:uppercase;margin-bottom:3px">Bulletin authentique</div>
      Scannez le QR code pour vérifier l'authenticité de ce bulletin en ligne.<br/>
      Réf. : <b>${bulletin_no}</b> · ${verifyUrl}<br/>
      ${d.signed ? `<span style="font-family:monospace;font-size:9px;color:#334155;font-weight:600">Signature SHA-256 : ${(d.signature || '').substring(0, 40)}…</span>` : `<span style="color:#7f1d1d;font-weight:800;font-size:10px">⚠ Bulletin non verrouillé — signature non générée</span>`}
    </div>
  </div>

  <div class="legal">
    Émis le ${printed} · Ce bulletin de paie est établi par ${co.name || 'ESPACE MAXO SARL'} conformément à la législation en vigueur au Bénin. À conserver par l&apos;employé et l&apos;employeur — Toute contestation doit être signalée dans un délai de 30 jours.
  </div>
`;
}

/** Reconstitue le bloc `extra` (valeurs calculées) depuis une fiche déjà sauvegardée (GET payslip). */
export function extraFromPayslip(d) {
  return {
    gross: d.gross || 0,
    daysPaid: d.days_paid ?? 0,
    bonusTot: d.bonus_total || 0,
    deductTot: d.deduction_total || 0,
    net: d.net_to_pay || 0,
    notes: d.notes || "",
    advance: d.advance_paid || 0,
    bonuses: d.bonuses || [],
    deductions: d.deductions || [],
    leaveTaken: d.leave_days_taken || 0,
    daysAbsent: d.days_absent || 0,
    periodStart: d.period_start_date || "",
    periodEnd: d.period_end_date || "",
    daysInPeriod: d.days_in_period,
    calendarDaysWorked: d.calendar_days_worked,
    daysInMonth: d.days_in_month,
    salaryBase: d.salary_base || 0,
    transportAmt: d.transport_allowance || 0,
    transportExempt: d.transport_exempt || 0,
    transportTaxable: d.transport_taxable || 0,
    cnssEmp: d.cnss_employee || 0,
    itsAmt: d.its_amount || 0,
    taxableNet: d.taxable_net || 0,
    gainsTotal: d.gains_total || d.gross || 0,
    totalRetenues: d.total_retenues || 0,
    isSubjectCNSS: !!d.subject_to_cnss,
    isSubjectITS: !!d.subject_to_its,
  };
}

export const PAYSLIP_PRINT_CSS = `
  @media print {
    @page { size: A4; margin: 7mm 10mm; }
    .no-break { page-break-inside: avoid; break-inside: avoid; }
  }
  * { box-sizing: border-box; }
  body {
    font-family: 'Helvetica Neue', 'Segoe UI', Arial, sans-serif;
    color: #0f172a;
    padding: 4px 10px; max-width: 800px; margin: auto;
    font-size: 11.5px; line-height: 1.4;
    font-weight: 500;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
    -webkit-font-smoothing: antialiased;
  }
  .brand { display: flex; align-items: center; justify-content: space-between; padding-bottom: 6px; margin-bottom: 8px; border-bottom: 2px solid #a92e2e; }
  .brand-l { display: flex; align-items: center; gap: 10px; }
  .brand-l img { height: 44px; width: auto; }
  .co-name { font-size: 13px; font-weight: 900; letter-spacing: 0.02em; color: #0F1F3D; margin: 0; }
  .co-meta { font-size: 10px; color: #1e293b; line-height: 1.4; margin-top: 1px; font-weight: 500; }
  .bulletin-box { text-align: right; }
  .bulletin-box .lbl { font-size: 9px; letter-spacing: 0.15em; text-transform: uppercase; color: #7f1d1d; font-weight: 800; }
  .bulletin-box .num { font-family: 'Courier New', monospace; font-size: 13px; font-weight: 900; color: #0F1F3D; letter-spacing: 0.05em; margin-top: 1px; }
  .bulletin-box .period { font-size: 10.5px; color: #1e293b; margin-top: 2px; text-transform: uppercase; letter-spacing: 0.1em; font-weight: 700; }
  h2 { font-size: 10px; text-transform: uppercase; letter-spacing: 0.13em; color: #7f1d1d; margin: 8px 0 4px; font-weight: 900; }
  .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
  .card { padding: 6px 9px; border: 1px solid #94a3b8; border-radius: 5px; background: #f8fafc; }
  .card-title { font-size: 9.5px; font-weight: 900; letter-spacing: 0.14em; text-transform: uppercase; color: #0F1F3D; padding-bottom: 3px; margin-bottom: 4px; border-bottom: 1.5px solid #64748b; }
  table { width: 100%; border-collapse: collapse; }
  .net-box { margin-top: 8px; background: #0F1F3D; color: white; border-radius: 6px; padding: 8px 14px; display: flex; justify-content: space-between; align-items: center; }
  .net-box .lbl { font-size: 10.5px; letter-spacing: 0.16em; text-transform: uppercase; font-weight: 800; opacity: 1; }
  .net-box .val { font-family: 'Courier New', monospace; font-size: 20px; font-weight: 900; letter-spacing: 0.02em; }
  .sig-block { margin-top: 10px; display: flex; justify-content: space-between; gap: 30px; font-size: 10.5px; color: #1e293b; font-weight: 500; }
  .sig { flex: 1; }
  .sig .line { border-top: 1.5px solid #334155; padding-top: 3px; margin-top: 22px; text-align: center; font-weight: 700; color: #0f172a; }
  .legal { margin-top: 8px; padding-top: 5px; border-top: 1px dotted #94a3b8; font-size: 9px; color: #334155; text-align: center; line-height: 1.45; font-weight: 500; }
`;
